import requests from '../requests.js';

const state = {
  id: null,
  email: null,
  firstName: null,
  lastName: null,
  shortName: null,
  color: null,
  gravatarUrl: null,
  avatarUrl: null,
  isSubscribed: null,
  passwordExist: null,
};

const getters = {
  /**
   * Return the id
   */
  getId(state) {
    return state.id;
  },

  /**
   * Return the email
   */
  getEmail(state) {
    return state.email;
  },

  /**
   * Return the first name
   */
  getFirstName(state) {
    return state.firstName;
  },

  /**
   * Return the last name
   */
  getLastName(state) {
    return state.lastName;
  },

  /**
   * Return the user full name
   */
  getName(state) {
    if (state.lastName) {
      return `${state.firstName} ${state.lastName}`;
    }
    return `${state.firstName}`;
  },

  /**
   * Return the short name
   */
  getShortName(state) {
    return state.shortName;
  },

  /**
   * Return the user color
   */
  getColor(state) {
    return state.color;
  },

  /**
   * Return the avatar url
   */
  getAvatarUrl(state) {
    return state.avatarUrl;
  },

  /**
   * Return the gravatar url
   */
  getGravatarUrl(state) {
    return state.gravatarUrl;
  },

  /**
   * Return whether the user is subscribed
   */
  getIsSubscribed(state) {
    return state.isSubscribed;
  },

  /**
   * Return whether the user is a beta user
   * @returns boolean
   */
  getIsBetaUser(state) {
    return state.betaUser;
  },

  /**
   * Returns whether the user is logged in or not - has no user name
   */
  getIsLoggedIn(state) {
    console.log(state);
    return state.firstName !== null || state.lastName !== null;
  },

  /**
   * Return google auth
   */
  getPasswordExist(state) {
    return state.passwordExist;
  },
};

const actions = {
  /**
   * Update the user name
   * @param {string}  firstName
   * @param {string}  lastName
   */
  updateName(context, { firstName, lastName }) {
    return requests.account
      .updateName({ firstName, lastName })
      .then(response => {
        const { initials } = response.data;
        const value = `${firstName} ${lastName}`;
        context.commit('setName', { value });
        context.commit('setShortName', { userInitials: initials });
        context.commit('setFirstName', { value: firstName });
        context.commit('setLastName', { value: lastName });
        return Promise.resolve(response);
      });
  },

  /**
   * Update the user email
   * @param {string} email
   * @param {string} emailConfirmation
   */
  updateEmail(context, { email, emailConfirmation }) {
    return requests.account
      .updateEmail({ email, emailConfirmation })
      .then(response => {
        context.commit('setEmail', { value: email });
        return Promise.resolve(response);
      });
  },

  /**
   * Update the user account password
   * @param {string} oldPassword
   * @param {string} password
   * @param {string} passwordConfirmation
   */
  updatePassword(context, { oldPassword, password, passwordConfirmation }) {
    return requests.account
      .updatePassword({ oldPassword, password, passwordConfirmation })
      .then(response => {
        context.commit('setPasswordExist', { value: true });
        return Promise.resolve(response);
      });
  },

  /**
   * Update the user account color
   * @param {string} color  The color in HTML color format e.g. '#5E5E5E'
   */
  updateColor(context, { color }) {
    return requests.account.updateColor({ color }).then(response => {
      context.commit('setColor', { value: color });
      return Promise.resolve(response);
    });
  },

  /**
   * Export a project
   * @param {string}  hash
   * @param {boolean} exportBoards
   * @param {array}   selectedBoards
   */
  export(context, { hash, exportBoards, selectedBoards }) {
    return requests.export
      .project({ hash, exportBoards, selectedBoards })
      .then(response => {
        return Promise.resolve(response);
      });
  },

  /**
   * Update the user account subscribed to newsletter attribute
   * @param {boolean}  value
   */
  updateSubscribedToNewsletter(context, { value }) {
    return requests.account
      .updateSubscribedToNewsletter({ value })
      .then(response => {
        context.commit('setIsSubscribed', { value });
        return Promise.resolve(response);
      });
  },

  /**
   * Delete the user gravatar (update the gravatar field)
   */
  deleteGravatar(context) {
    return requests.account.deleteGravatar().then(response => {
      context.commit('setGravatarUrl', { value: null });
      return Promise.resolve(response);
    });
  },

  /**
   * Upload a user avatar
   * @param {string} image
   */
  uploadAvatar(context, { image }) {
    return requests.account.uploadAvatar({ image }).then(response => {
      const { file } = response.data;
      context.commit('setAvatarUrl', { value: file });
      return Promise.resolve(response);
    });
  },

  /**
   * Delete a user avatar
   */
  deleteAvatar(context) {
    return requests.account.deleteAvatar().then(response => {
      context.commit('setAvatarUrl', { value: null });
      return Promise.resolve(response);
    });
  },
};

const mutations = {
  setAccount(state, { value }) {
    Object.assign(state, value);
  },

  /**
   * Set the user email
   */
  setEmail(state, { value }) {
    state.email = value;
  },

  /**
   * Set the user name
   */
  setName(state, { value }) {
    state.name = value;
  },

  /**
   * Set the user first name
   */
  setFirstName(state, { value }) {
    state.firstName = value;
  },

  /**
   * Set the user last name
   */
  setLastName(state, { value }) {
    state.lastName = value;
  },

  /**
   * Set the user short name
   */
  setShortName(state, { userInitials }) {
    state.shortName = userInitials;
  },

  /**
   * Set whether the user is subscribed to newsletter
   */
  setIsSubscribed(state, { value }) {
    state.isSubscribed = value;
  },

  /**
   * Set the gravatar url
   */
  setGravatarUrl(state, { value }) {
    state.gravatarUrl = value;
  },

  /**
   * Set the avatar url
   */
  setAvatarUrl(state, { value }) {
    state.avatarUrl = value;
  },

  /**
   * Set the passwordExist
   */
  setPasswordExist(state, { value }) {
    state.passwordExist = value;
  },

  /**
   * Set user color
   */
  setColor(state, { value }) {
    state.color = value;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
